import { Tabs } from 'foundations-components/transitional/components/Tabs';
import { isGenericChannelId } from 'conversations-thread-data/channel-configuration/public/types';
import { Flex } from 'foundations-components/components/Flex';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAllowedMessageChannelIds } from '../../../reply-editor/protected/hooks';
import { CommentTab } from './CommentTab';
import { CustomTab } from './CustomTab';
import ChannelSwitchDropdown from './ChannelSwitchDropdown';
import { getUrlFragmentFromConfigKey } from '../../../editor-utilities/protected/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getSelectedEditorKey({
  allowedMessageChannelIds,
  originalGenericChannelId
}) {
  if (!allowedMessageChannelIds) {
    return null;
  }
  if (allowedMessageChannelIds.includes(originalGenericChannelId)) {
    return originalGenericChannelId;
  }
  return allowedMessageChannelIds[0];
}
function getSelectedTab({
  allowedMessageChannelIds,
  editorFragment,
  hideComments,
  customEditorViews
}) {
  if (!allowedMessageChannelIds.length || !editorFragment) return 'channel';
  const allowedChannelFragments = allowedMessageChannelIds.map(id => getUrlFragmentFromConfigKey({
    editorConfigKey: id
  }));

  // If it's an allowed channel fragment, keep default 'channel' tab
  if (allowedChannelFragments.includes(editorFragment)) return 'channel';

  // Check if it's a valid other view
  const otherViews = [...((customEditorViews === null || customEditorViews === void 0 ? void 0 : customEditorViews.views.map(view => view.fragment)) || []), ...(!hideComments ? ['comment'] : [])];
  if (otherViews.includes(editorFragment)) {
    return editorFragment;
  }
  return 'channel';
}
const EditorTabBar = ({
  children
}) => /*#__PURE__*/_jsx(Flex, {
  flexBasis: "row",
  justifyContent: "space-between",
  children: /*#__PURE__*/_jsx(Flex, {
    flexGrow: "1",
    children: children
  })
});
EditorTabBar.displayName = 'EditorTabBar';
export const EditorTabs = ({
  associatedObjectId,
  associatedObjectTypeId,
  callingSource,
  currentChannelId,
  customEditorViews,
  disableCalling,
  editorFragment,
  hideComments,
  inboxId,
  isKnownVisitor,
  onSelectedChange,
  originalGenericChannelId,
  subjectId,
  threadId,
  handleFirstClick
}) => {
  const [channelDropdownIsOpen, setChannelDropdownIsOpen] = useState(false);
  const {
    allowedMessageChannelIds
  } = useAllowedMessageChannelIds({
    originalGenericChannelId
  });
  const defaultChannelId = useMemo(() => getSelectedEditorKey({
    allowedMessageChannelIds,
    originalGenericChannelId
  }), [allowedMessageChannelIds, originalGenericChannelId]);
  const [selectedChannelId, setSelectedChannelId] = useState(() => isGenericChannelId(currentChannelId) ? currentChannelId : defaultChannelId);
  const [selectedTab, setSelectedTab] = useState(() => getSelectedTab({
    allowedMessageChannelIds,
    editorFragment,
    hideComments,
    customEditorViews
  }));
  useEffect(() => {
    // Update the selected tab when the editorFragment changes
    const newSelectedTab = getSelectedTab({
      allowedMessageChannelIds,
      editorFragment,
      hideComments,
      customEditorViews
    });
    if (newSelectedTab === editorFragment && newSelectedTab !== selectedTab) {
      setSelectedTab(newSelectedTab);
    }
  }, [allowedMessageChannelIds, customEditorViews, editorFragment, hideComments, selectedTab]);
  useEffect(() => {
    // when default channel id has changed (i.e. switching threads), reset the selected state
    setSelectedChannelId(defaultChannelId);
  }, [defaultChannelId]);
  useEffect(() => {
    if (isGenericChannelId(currentChannelId)) {
      setSelectedChannelId(currentChannelId);
    }
  }, [currentChannelId]);
  const handleTabClick = useCallback(event => {
    handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('EditorTabs');
    const tabId = event.target.value;
    if (tabId === 'channel') {
      if (selectedTab === 'channel') {
        // when clicking the channel tab when it's already selected, open the dropdown
        setChannelDropdownIsOpen(true);
      } else {
        // when switching to the channel tab from another tab, call onSelectedChange with the selected channel
        setSelectedTab(String(tabId));
        const fragment = getUrlFragmentFromConfigKey({
          editorConfigKey: selectedChannelId
        });
        onSelectedChange(fragment);
      }
    } else if (tabId !== selectedTab) {
      setSelectedTab(String(tabId));
      onSelectedChange(String(tabId));
    }
  }, [handleFirstClick, onSelectedChange, selectedChannelId, selectedTab]);
  const handleSelectDropdownOption = useCallback(channelId => {
    setSelectedChannelId(channelId);
    const fragment = getUrlFragmentFromConfigKey({
      editorConfigKey: channelId
    });
    onSelectedChange(fragment);
  }, [onSelectedChange]);
  return /*#__PURE__*/_jsxs(Tabs, {
    Wrapper: EditorTabBar,
    defaultSelected: selectedTab,
    selected: selectedTab,
    onSelectedChange: handleTabClick,
    children: [selectedChannelId && (allowedMessageChannelIds === null || allowedMessageChannelIds === void 0 ? void 0 : allowedMessageChannelIds.length) > 0 && /*#__PURE__*/_jsx(ChannelSwitchDropdown, {
      allowedMessageChannelIds: allowedMessageChannelIds,
      associatedObjectId: associatedObjectId,
      associatedObjectTypeId: associatedObjectTypeId,
      callingSource: callingSource,
      currentChannelId: currentChannelId,
      dropdownIsOpen: channelDropdownIsOpen,
      hasCalling: !disableCalling,
      inboxId: inboxId,
      isKnownVisitor: isKnownVisitor,
      onSelectDropdownOption: handleSelectDropdownOption,
      selectedChannelId: selectedChannelId,
      setChannelDropdownIsOpen: setChannelDropdownIsOpen,
      subjectId: subjectId,
      tabId: "channel" // This prop is only used by the Tabs component to determine which tab is selected
      ,
      threadId: threadId
    }), !hideComments && /*#__PURE__*/_jsx(CommentTab, {
      tabId: "comment"
    }), customEditorViews === null || customEditorViews === void 0 ? void 0 : customEditorViews.views.map(view => /*#__PURE__*/_jsx(CustomTab, {
      tabId: view.fragment,
      channelKey: view.key,
      tabLabel: view.label
    }, `custom-tab-${view.key}`))]
  });
};
EditorTabs.displayName = 'EditorTabs';